import { useEffect, useRef, useCallback, useState } from "react";

/**
 * Hook dedicado para o Escritório Virtual (EVA).
 * Conecta ao /ws/office do brain-api para receber estado em tempo real.
 * Inclui fallback REST via tRPC quando WS não conecta.
 */

export interface OfficeAgent {
  agent_id: string;
  display_name: string;
  status: string;
  current_task: string | null;
  current_tool: string | null;
  current_connector: string | null;
  last_activity: number;
  last_message: string | null;
  col: number;
  row: number;
  zone: string;
  desk_type: string;
  error_count: number;
  tasks_completed_today: number;
}

export interface OfficeChatMessage {
  id: string;
  sender: string;
  channel: string;
  content: string;
  timestamp: number;
  reply_to: string | null;
}

export interface OfficeStats {
  total_agents: number;
  by_status: Record<string, number>;
  total_tasks_today: number;
  total_chat_messages: number;
}

export interface OfficeZone {
  agents: number;
  active: number;
  errors: number;
}

export interface OfficeState {
  agents: Record<string, OfficeAgent>;
  zones: Record<string, OfficeZone>;
  chat: OfficeChatMessage[];
  stats: OfficeStats;
}

/** Evento SSE de execução de agente no escritório */
export interface OfficeAgentEvent {
  type: string;
  agent_id: string;
  data: Record<string, unknown>;
  timestamp: number;
}

/** Evento de execução individual de um agente (vindo do SSE /execute) */
export interface AgentExecEvent {
  type: string;
  content?: string;
  tool?: string;
  connector?: string;
  agent_id?: string;
  task_id?: string;
  timestamp?: number;
}

type ConnectionState = "connecting" | "connected" | "disconnected" | "reconnecting";

interface UseOfficeWSOptions {
  /** URL do brain-api WebSocket (default: auto-detect) */
  brainWsUrl?: string;
  /** Auto-reconnect (default: true) */
  autoReconnect?: boolean;
  /** Callback quando estado completo é recebido */
  onFullState?: (state: OfficeState) => void;
  /** Callback quando um agente é atualizado */
  onAgentUpdate?: (agentId: string, changes: Partial<OfficeAgent>) => void;
  /** Callback quando mensagem de chat é recebida */
  onChatMessage?: (message: OfficeChatMessage) => void;
  /** Callback quando evento de execução de agente é recebido */
  onAgentExecEvent?: (event: OfficeAgentEvent) => void;
}

export function useOfficeWS(options: UseOfficeWSOptions = {}) {
  const {
    autoReconnect = true,
    onFullState,
    onAgentUpdate,
    onChatMessage,
    onAgentExecEvent,
  } = options;

  const [connectionState, setConnectionState] = useState<ConnectionState>("disconnected");
  const [officeState, setOfficeState] = useState<OfficeState | null>(null);
  const [agentExecEvents, setAgentExecEvents] = useState<OfficeAgentEvent[]>([]);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectCountRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const heartbeatTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // REST fallback function — injected by parent component via setRestFallback
  const restFallbackRef = useRef<((sender: string, content: string, channel: string) => Promise<unknown>) | null>(null);

  const setRestFallback = useCallback((fn: (sender: string, content: string, channel: string) => Promise<unknown>) => {
    restFallbackRef.current = fn;
  }, []);

  const getWsUrl = useCallback(() => {
    if (options.brainWsUrl) return options.brainWsUrl;
    // @ts-ignore - Vite env vars
    const brainUrl = (typeof import.meta !== "undefined" && (import.meta as any).env?.VITE_BRAIN_API_URL)
      || (typeof import.meta !== "undefined" && (import.meta as any).env?.VITE_API_URL)
      || "";
    if (brainUrl) {
      const wsProtocol = brainUrl.startsWith("https") ? "wss:" : "ws:";
      const host = brainUrl.replace(/^https?:\/\//, "").replace(/\/$/, "");
      return `${wsProtocol}//${host}/ws/office`;
    }
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    return `${protocol}//${window.location.host}/ws/office`;
  }, [options.brainWsUrl]);

  const stopHeartbeat = useCallback(() => {
    if (heartbeatTimerRef.current) {
      clearInterval(heartbeatTimerRef.current);
      heartbeatTimerRef.current = null;
    }
  }, []);

  const startHeartbeat = useCallback(() => {
    stopHeartbeat();
    heartbeatTimerRef.current = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: "ping" }));
      }
    }, 25000);
  }, [stopHeartbeat]);

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    setConnectionState("connecting");
    const ws = new WebSocket(getWsUrl());

    ws.onopen = () => {
      setConnectionState("connected");
      reconnectCountRef.current = 0;
      startHeartbeat();
      console.log("[OfficeWS] Conectado via WebSocket");
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        if (data.type === "pong") return;

        if (data.type === "full_state") {
          const state: OfficeState = {
            agents: data.agents || {},
            zones: data.zones || {},
            chat: data.chat || [],
            stats: data.stats || { total_agents: 0, by_status: {}, total_tasks_today: 0, total_chat_messages: 0 },
          };
          setOfficeState(state);
          onFullState?.(state);
        }

        if (data.type === "agent_update") {
          const agentId = data.agent_id;
          const changes = data.changes;
          setOfficeState((prev) => {
            if (!prev) return prev;
            const agent = prev.agents[agentId];
            if (!agent) return prev;
            return {
              ...prev,
              agents: {
                ...prev.agents,
                [agentId]: { ...agent, ...changes },
              },
            };
          });
          onAgentUpdate?.(agentId, changes);
        }

        if (data.type === "chat_message") {
          const msg = data.message as OfficeChatMessage;
          setOfficeState((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              chat: [...prev.chat.slice(-49), msg],
            };
          });
          onChatMessage?.(msg);
        }

        // Agent execution events (from /execute command)
        if (data.type === "agent_exec_event") {
          const execEvent: OfficeAgentEvent = {
            type: data.event_type || data.type,
            agent_id: data.agent_id,
            data: data.data || {},
            timestamp: data.timestamp || Date.now(),
          };
          setAgentExecEvents((prev) => [...prev.slice(-99), execEvent]);
          onAgentExecEvent?.(execEvent);
        }
      } catch {
        // Ignore malformed messages
      }
    };

    ws.onclose = () => {
      setConnectionState("disconnected");
      stopHeartbeat();

      if (autoReconnect && reconnectCountRef.current < 10) {
        const delay = Math.min(1000 * Math.pow(2, reconnectCountRef.current), 30000);
        setConnectionState("reconnecting");
        reconnectTimerRef.current = setTimeout(() => {
          reconnectCountRef.current++;
          connect();
        }, delay);
      }
    };

    ws.onerror = () => {
      // onclose will handle reconnection
    };

    wsRef.current = ws;
  }, [getWsUrl, autoReconnect, onFullState, onAgentUpdate, onChatMessage, onAgentExecEvent, startHeartbeat, stopHeartbeat]);

  const disconnect = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    stopHeartbeat();
    reconnectCountRef.current = 10; // prevent auto-reconnect
    if (wsRef.current) {
      wsRef.current.close(1000, "User disconnect");
      wsRef.current = null;
    }
    setConnectionState("disconnected");
  }, [stopHeartbeat]);

  /**
   * Envia mensagem no chat do escritório.
   * Tenta via WebSocket primeiro; se não conectado, usa fallback REST via tRPC.
   */
  const sendChat = useCallback(async (content: string, channel: string = "general") => {
    // Tentar via WebSocket primeiro
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: "chat",
        sender: "user",
        content,
        channel,
      }));
      // Adicionar mensagem otimisticamente ao estado local
      const optimisticMsg: OfficeChatMessage = {
        id: `local-${Date.now()}`,
        sender: "user",
        channel,
        content,
        timestamp: Date.now() / 1000,
        reply_to: null,
      };
      setOfficeState((prev) => {
        if (!prev) return prev;
        return { ...prev, chat: [...prev.chat.slice(-49), optimisticMsg] };
      });
      return;
    }
    // Fallback REST via tRPC
    if (restFallbackRef.current) {
      try {
        await restFallbackRef.current("user", content, channel);
        console.log("[OfficeWS] Chat enviado via REST fallback");
        // Adicionar mensagem otimisticamente
        const optimisticMsg: OfficeChatMessage = {
          id: `local-${Date.now()}`,
          sender: "user",
          channel,
          content,
          timestamp: Date.now() / 1000,
          reply_to: null,
        };
        setOfficeState((prev) => {
          if (!prev) return prev;
          return { ...prev, chat: [...prev.chat.slice(-49), optimisticMsg] };
        });
      } catch (err) {
        console.error("[OfficeWS] Falha no REST fallback:", err);
      }
      return;
    }
    console.warn("[OfficeWS] Sem conexão WS e sem REST fallback — mensagem perdida");
  }, []);

  const requestState = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "get_state" }));
    }
  }, []);

  const clearAgentExecEvents = useCallback(() => {
    setAgentExecEvents([]);
  }, []);

  // ─── Agent Execution via SSE ─────────────────────────────────
  const [executingAgents, setExecutingAgents] = useState<Record<string, boolean>>({});
  const [agentExecEventsByAgent, setAgentExecEventsByAgent] = useState<Record<string, AgentExecEvent[]>>({});
  const abortControllersRef = useRef<Record<string, AbortController>>({});

  const executeAgent = useCallback(async (agentId: string, task: string, execConfig?: { url: string; apiKey: string }) => {
    if (executingAgents[agentId]) return;

    setExecutingAgents((prev) => ({ ...prev, [agentId]: true }));
    setAgentExecEventsByAgent((prev) => ({ ...prev, [agentId]: [] }));

    const controller = new AbortController();
    abortControllersRef.current[agentId] = controller;

    try {
      let url: string;
      let apiKey: string;
      if (execConfig) {
        url = execConfig.url;
        apiKey = execConfig.apiKey;
      } else {
        // @ts-ignore
        const brainUrl = (typeof import.meta !== "undefined" && (import.meta as any).env?.VITE_BRAIN_API_URL)
          || "https://ym-brain-api-production.up.railway.app";
        url = `${brainUrl}/office/agents/${agentId}/execute`;
        apiKey = "";
      }

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(apiKey ? { "X-API-Key": apiKey } : {}),
        },
        body: JSON.stringify({ task, max_steps: 15 }),
        signal: controller.signal,
      });

      if (!response.ok || !response.body) {
        throw new Error(`Execute failed: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          try {
            const event: AgentExecEvent = JSON.parse(line.slice(6));
            event.agent_id = agentId;
            setAgentExecEventsByAgent((prev) => ({
              ...prev,
              [agentId]: [...(prev[agentId] || []), event],
            }));
          } catch {
            // Ignore malformed events
          }
        }
      }
    } catch (err: any) {
      if (err.name !== "AbortError") {
        console.error(`[OfficeWS] Execute agent ${agentId} failed:`, err);
        setAgentExecEventsByAgent((prev) => ({
          ...prev,
          [agentId]: [...(prev[agentId] || []), { type: "error", content: err.message || "Erro desconhecido" }],
        }));
      }
    } finally {
      setExecutingAgents((prev) => ({ ...prev, [agentId]: false }));
      delete abortControllersRef.current[agentId];
    }
  }, [executingAgents]);

  const stopAgentExecution = useCallback((agentId: string) => {
    const controller = abortControllersRef.current[agentId];
    if (controller) {
      controller.abort();
      delete abortControllersRef.current[agentId];
    }
    setExecutingAgents((prev) => ({ ...prev, [agentId]: false }));
  }, []);

  // Auto-connect on mount
  useEffect(() => {
    connect();
    return () => {
      disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    connectionState,
    officeState,
    isConnected: connectionState === "connected",
    connect,
    disconnect,
    sendChat,
    requestState,
    setRestFallback,
    agentExecEvents,
    clearAgentExecEvents,
    executeAgent,
    stopAgentExecution,
    executingAgents,
    agentExecEventsByAgent,
  };
}
